import {ref} from 'vue';
import isDeepEqual from 'lodash-es/isEqual';
import {defineStore} from 'pinia';
import type {LocationQuery, LocationQueryValue} from 'vue-router';
import type {Location} from '@malt/nuxt-common-components-module/types';
import {RetrieveAvailableFiltersAdminFilterEnum, RetrieveAvailableFiltersBadgeEnum, RetrieveAvailableFiltersExpEnum} from '@api';
import {
    sanitizeBooleanParam,
    sanitizeEnumArrayParam,
    sanitizeSingleEnumParam,
    sanitizeSingleIntegerParam,
    sanitizeSingleNumberParam,
    sanitizeSingleStringParam,
    sanitizeStringArrayParam,
} from '~/utils/query-param-sanitizer';

function isRemoteAllowed(suggestion: Location): suggestion is Location {
    return Boolean(suggestion && (suggestion as any).remoteAllowed);
}

function isGeo(suggestion: Location): suggestion is Location {
    return Boolean(suggestion && !isRemoteAllowed(suggestion));
}

type GeoLocationParams = {
    location: string;
    street?: string;
    street2?: string;
    city?: string;
    zipCode?: string;
    administrativeAreaLevel4?: string;
    administrativeAreaLevel4Code?: string;
    administrativeAreaLevel3?: string;
    administrativeAreaLevel3Code?: string;
    administrativeAreaLevel2?: string;
    administrativeAreaLevel2Code?: string;
    administrativeAreaLevel1?: string;
    administrativeAreaLevel1Code?: string;
    country?: string;
    countryCode?: string;
    region?: string;
    lat?: number;
    lon?: number;
};

type RemoteAllowedLocationParams = {
    location: string;
    remoteAllowed?: boolean;
};

export type LocationParams = GeoLocationParams | RemoteAllowedLocationParams;

export function isRemoteAllowedParams(location: LocationParams): location is GeoLocationParams {
    return 'remoteAllowed' in location;
}

export function isGeoLocationParams(location: LocationParams): location is GeoLocationParams {
    return !isRemoteAllowedParams(location);
}

export const RejectReasonValues = {
    ProfileViewsAbuseLimit: 'profile-views-limit',
    ProfileViewsServiceLevelLimit: 'profile-views-service-level-limit',
} as const;
export type RejectReason = (typeof RejectReasonValues)[keyof typeof RejectReasonValues];

export interface SearchParams {
    // query keywords
    query: string;

    // location params
    locationDetails?: LocationParams;

    // filters params
    languages: string[];
    specialities: string[];
    categories: string[];
    businessSectors: string[];
    badges: RetrieveAvailableFiltersBadgeEnum[];
    experienceLevels: RetrieveAvailableFiltersExpEnum[];
    adminFilter: RetrieveAvailableFiltersAdminFilterEnum[];
    minPrice?: number;
    maxPrice?: number;

    page?: number;

    rejectReason?: RejectReason;
    rejectedUrl?: string;
}

type UnknownParams = {[p: string]: LocationQueryValue | LocationQueryValue[]};

export const useSearchUrlStore = defineStore('search-url', () => {
    const searchUrlParams = ref<SearchParams>({
        query: '',
        languages: [],
        specialities: [],
        categories: [],
        businessSectors: [],
        badges: [],
        experienceLevels: [],
        adminFilter: [],
    });
    const unknownParams = ref<UnknownParams>();

    function update(query: LocationQuery) {
        const {
            q,
            p,
            lang,
            speciality,
            category,
            businessSector,
            badge,
            exp,
            adminFilter,
            minPrice,
            maxPrice,
            // location params
            remoteAllowed,
            location,
            street,
            street2,
            city,
            zipCode,
            administrativeAreaLevel4,
            administrativeAreaLevel4Code,
            administrativeAreaLevel3,
            administrativeAreaLevel3Code,
            administrativeAreaLevel2,
            administrativeAreaLevel2Code,
            administrativeAreaLevel1,
            administrativeAreaLevel1Code,
            country,
            countryCode,
            region,
            lat,
            lon,
            rejectReason,
            rejectedUrl,
            // unknown parameters
            ...additionalParameters
        } = query;
        const newSearchUrlParams: SearchParams = {
            query: q as string,
            locationDetails: parseUrlQueryParamsForLocation({
                remoteAllowed,
                location,
                street,
                street2,
                city,
                zipCode,
                administrativeAreaLevel4,
                administrativeAreaLevel4Code,
                administrativeAreaLevel3,
                administrativeAreaLevel3Code,
                administrativeAreaLevel2,
                administrativeAreaLevel2Code,
                administrativeAreaLevel1,
                administrativeAreaLevel1Code,
                country,
                countryCode,
                region,
                lat,
                lon,
            }),
            page: sanitizeSingleIntegerParam(p),
            languages: sanitizeStringArrayParam(lang),
            specialities: sanitizeStringArrayParam(speciality),
            categories: sanitizeStringArrayParam(category),
            businessSectors: sanitizeStringArrayParam(businessSector),
            badges: sanitizeEnumArrayParam(badge, RetrieveAvailableFiltersBadgeEnum),
            experienceLevels: sanitizeEnumArrayParam(exp, RetrieveAvailableFiltersExpEnum),
            adminFilter: sanitizeEnumArrayParam(adminFilter, RetrieveAvailableFiltersAdminFilterEnum),
            minPrice: sanitizeSingleIntegerParam(minPrice),
            maxPrice: sanitizeSingleIntegerParam(maxPrice),
            rejectReason: sanitizeSingleEnumParam(rejectReason, RejectReasonValues),
            rejectedUrl: sanitizeSingleStringParam(rejectedUrl),
        };
        // we prevent watchers to be updated is data doesn't change
        if (!isDeepEqual(searchUrlParams.value, newSearchUrlParams)) {
            searchUrlParams.value = newSearchUrlParams;
        }
        unknownParams.value = additionalParameters;
    }

    function toOpenApiRequestParams() {
        const params = searchUrlParams.value;
        return {
            q: params.query,
            ...(params.languages.length ? {lang: params.languages} : {}),
            ...(params.specialities.length ? {speciality: params.specialities} : {}),
            ...(params.categories.length ? {category: params.categories} : {}),
            ...(params.businessSectors.length ? {businessSector: params.businessSectors} : {}),
            ...(params.badges.length ? {badge: params.badges} : {}),
            ...(params.experienceLevels.length ? {exp: params.experienceLevels} : {}),
            ...(params.minPrice ? {minPrice: params.minPrice} : {}),
            ...(params.maxPrice ? {maxPrice: params.maxPrice} : {}),
            ...(params.adminFilter.length ? {adminFilter: params.adminFilter} : {}),
            ...(params.page && params.page > 1 ? {p: params.page} : {}),
            ...(params.locationDetails ?? {}),
        };
    }

    /**
     * vue-router don't accept boolean parameters, we must convert them into string.
     */
    function toRouterQueryParams() {
        const searchParams = Object.entries(toOpenApiRequestParams()).map(([name, value]) => [name, typeof value === 'boolean' ? `${value}` : value]);
        const additionalParams = unknownParams.value ? Object.entries(unknownParams.value) : [];
        return Object.fromEntries([searchParams, additionalParams].flat());
    }

    /**
     * @param overrideParams Can add new query param, replace an existing query param, or remove a query param (param = undefined)
     */
    function toUrlQueryParams(overrideParams: {[key: string]: string | undefined} | undefined = undefined) {
        const parameters = toOpenApiRequestParams();
        const unknownParameters = unknownParams.value ?? {};
        const overrideParameters = overrideParams || {};

        const allParams = takeOnlyTruthlyValues(Object.entries({...parameters, ...unknownParameters, ...overrideParameters}));

        const queryParams = new URLSearchParams();

        for (const [name, value] of allParams) {
            if (Array.isArray(value)) {
                for (const item of value) {
                    queryParams.append(`${name}`, `${item}`);
                }
            } else {
                queryParams.append(`${name}`, `${value}`);
            }
        }

        return queryParams;
    }

    function parseUrlQueryParamsForLocation(query: {
        remoteAllowed: LocationQueryValue | LocationQueryValue[];
        location: LocationQueryValue | LocationQueryValue[];
        street: LocationQueryValue | LocationQueryValue[];
        street2: LocationQueryValue | LocationQueryValue[];
        city: LocationQueryValue | LocationQueryValue[];
        zipCode: LocationQueryValue | LocationQueryValue[];
        administrativeAreaLevel4: LocationQueryValue | LocationQueryValue[];
        administrativeAreaLevel4Code: LocationQueryValue | LocationQueryValue[];
        administrativeAreaLevel3: LocationQueryValue | LocationQueryValue[];
        administrativeAreaLevel3Code: LocationQueryValue | LocationQueryValue[];
        administrativeAreaLevel2: LocationQueryValue | LocationQueryValue[];
        administrativeAreaLevel2Code: LocationQueryValue | LocationQueryValue[];
        administrativeAreaLevel1: LocationQueryValue | LocationQueryValue[];
        administrativeAreaLevel1Code: LocationQueryValue | LocationQueryValue[];
        country: LocationQueryValue | LocationQueryValue[];
        countryCode: LocationQueryValue | LocationQueryValue[];
        region: LocationQueryValue | LocationQueryValue[];
        lat: LocationQueryValue | LocationQueryValue[];
        lon: LocationQueryValue | LocationQueryValue[];
    }): LocationParams | undefined {
        const location = sanitizeSingleStringParam(query.location);
        if (location) {
            if (sanitizeBooleanParam(query.remoteAllowed)) {
                return {
                    location,
                    remoteAllowed: true,
                };
            } else {
                return {
                    location,
                    street: sanitizeSingleStringParam(query.street),
                    street2: sanitizeSingleStringParam(query.street2),
                    city: sanitizeSingleStringParam(query.city),
                    zipCode: sanitizeSingleStringParam(query.zipCode),
                    administrativeAreaLevel4: sanitizeSingleStringParam(query.administrativeAreaLevel4),
                    administrativeAreaLevel4Code: sanitizeSingleStringParam(query.administrativeAreaLevel4Code),
                    administrativeAreaLevel3: sanitizeSingleStringParam(query.administrativeAreaLevel3),
                    administrativeAreaLevel3Code: sanitizeSingleStringParam(query.administrativeAreaLevel3Code),
                    administrativeAreaLevel2: sanitizeSingleStringParam(query.administrativeAreaLevel2),
                    administrativeAreaLevel2Code: sanitizeSingleStringParam(query.administrativeAreaLevel2Code),
                    administrativeAreaLevel1: sanitizeSingleStringParam(query.administrativeAreaLevel1),
                    administrativeAreaLevel1Code: sanitizeSingleStringParam(query.administrativeAreaLevel1Code),
                    country: sanitizeSingleStringParam(query.country),
                    countryCode: sanitizeSingleStringParam(query.countryCode),
                    region: sanitizeSingleStringParam(query.region),
                    lat: sanitizeSingleNumberParam(query.lat),
                    lon: sanitizeSingleNumberParam(query.lon),
                };
            }
        }
    }

    const setGeoLocationSuggestionDetails = (location: Location): LocationParams => ({
        location: location.formattedAddress ?? '',
        street: location.street ?? undefined,
        street2: location.street2 ?? undefined,
        city: location.city ?? undefined,
        zipCode: location.zipCode ?? undefined,
        administrativeAreaLevel4: location.administrativeAreaLevel4 ?? undefined,
        administrativeAreaLevel4Code: location.administrativeAreaLevel4Code ?? undefined,
        administrativeAreaLevel3: location.administrativeAreaLevel3 ?? undefined,
        administrativeAreaLevel3Code: location.administrativeAreaLevel3Code ?? undefined,
        administrativeAreaLevel2: location.administrativeAreaLevel2 ?? undefined,
        administrativeAreaLevel2Code: location.administrativeAreaLevel2Code ?? undefined,
        administrativeAreaLevel1: location.administrativeAreaLevel1 ?? undefined,
        administrativeAreaLevel1Code: location.administrativeAreaLevel1Code ?? undefined,
        country: location.country ?? undefined,
        countryCode: location.countryCode ?? undefined,
        region: location.region ?? undefined,
        lat: location.loc?.lat,
        lon: location.loc?.lon,
    });

    function updateUrlParams(query: string, location: Location | null): void {
        searchUrlParams.value.query = query;
        searchUrlParams.value.page = undefined;

        if (!location) {
            return;
        }

        if (isRemoteAllowed(location) && location.formattedAddress) {
            searchUrlParams.value.locationDetails = {
                location: location.formattedAddress,
                remoteAllowed: true,
            };
            return;
        }
        if (isGeo(location)) {
            searchUrlParams.value.locationDetails = setGeoLocationSuggestionDetails(location);
        }
    }

    function takeOnlyTruthlyValues(array: any[]): any[] {
        return array.filter(([_, value]) => !!value);
    }

    return {
        searchUrlParams,
        update,
        updateUrlParams,
        toOpenApiRequestParams,
        toRouterQueryParams,
        toUrlQueryParams,
    };
});
